import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useRef, useState, type ReactElement, type ReactNode } from "react";
import { ContentHeader, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { GnreFileLink, GnreView } from "@cx/contracts";
import { formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { downloadApiFile } from "../../download";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { dateTimeBR } from "../invoices/InvoiceEvents";
import { CancelGuideDialog, DueDateDialog, IssueDialog, PayDialog, useProofUpload } from "./GnreDialogs";
import { GNRE_STATUS_LABEL, GNRE_STATUS_TONE, collectionLineLabel, guideTitle, periodLabel } from "./labels";

const Info = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <dt className="col-sm-4">{label}</dt>
    <dd className="col-sm-8">{children}</dd>
  </>
);

type Dialog = "issue" | "pay" | "due" | "cancel" | null;

/**
 * Guia GNRE/DARE-SP (T2.11): dados para emissão no portal, PDF de conferência, XML (GNRE), linha digitável, baixa da
 * conta a pagar com comprovante e cancelamento (estorno). Guia cancelada fica no histórico.
 */
export function GnrePage(): ReactElement {
  const { id } = useParams({ strict: false }) as { id: string };
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const key = ["gnre", companyId, "view", id];
  const guide = useQuery({ queryKey: key, queryFn: () => api<GnreView>(`/gnre/${id}`) });
  const done = (g: GnreView) => {
    setDialog(null);
    qc.setQueryData(key, g);
    void qc.invalidateQueries({ queryKey: ["gnre", companyId] });
    void qc.invalidateQueries({ queryKey: ["titles", companyId] });
  };
  const onError = (e: Error) => toast("danger", errorMessage(e));
  const pdf = useMutation({
    mutationFn: () => api<GnreFileLink>(`/gnre/${id}/pdf`),
    onSuccess: (r) => window.location.assign(r.url),
    onError,
  });
  const proofLink = useMutation({
    mutationFn: () => api<GnreFileLink>(`/gnre/${id}/proof`),
    onSuccess: (r) => window.location.assign(r.url),
    onError,
  });
  const xml = useMutation({
    mutationFn: () => downloadApiFile(`/gnre/${id}/xml`, companyId, `gnre-${id}.xml`),
    onError,
  });
  const proof = useProofUpload(id);
  const g = guide.data;
  const canUpdate = can(perms, "gnre", "update");
  const canPay = canUpdate && can(perms, "payables", "update");
  const open = g && (g.status === "pending" || g.status === "issued");
  return (
    <>
      <ContentHeader
        title={g ? `${guideTitle(g)} · NF-e ${g.invoiceNumber}` : "Guia GNRE/DARE"}
        breadcrumb={["Vendas", "Guias GNRE/DARE", g ? g.uf : ""]}
        actions={
          g && (
            <>
              <Link to="/vendas/gnre" className="btn btn-outline-secondary">
                Voltar
              </Link>
              {g.status !== "canceled" && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={pdf.isPending}
                  onClick={() => pdf.mutate()}
                >
                  Imprimir
                </button>
              )}
              {g.xmlAvailable && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={xml.isPending}
                  onClick={() => xml.mutate()}
                >
                  Baixar XML
                </button>
              )}
              {/* O vencimento muda também a conta a pagar (gnre:update + payables:update). */}
              {canPay && g.editable && (
                <button type="button" className="btn btn-outline-secondary" onClick={() => setDialog("due")}>
                  Alterar vencimento
                </button>
              )}
              {canUpdate && open && (
                <button type="button" className="btn btn-outline-primary" onClick={() => setDialog("issue")}>
                  {g.status === "pending" ? "Marcar como emitida" : "Corrigir linha digitável"}
                </button>
              )}
              {canPay && open && (
                <button type="button" className="btn btn-success" onClick={() => setDialog("pay")}>
                  Marcar como paga
                </button>
              )}
              {canPay && open && (
                <button type="button" className="btn btn-outline-danger" onClick={() => setDialog("cancel")}>
                  Cancelar guia
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={guide.isLoading} error={guide.error} />
      {g && (
        <div className="row">
          <div className="col-xl-8">
            {g.invoiceCanceled && g.status !== "canceled" && (
              <div className="alert alert-warning" role="alert">
                A NF-e desta guia foi cancelada. Se a guia não foi paga, cancele-a; se já foi paga, peça a
                restituição ao estado de {g.uf}.
              </div>
            )}
            {g.environment === 2 && (
              <div className="alert alert-info py-2">
                NF-e de homologação: guia sem valor fiscal, só para teste.
              </div>
            )}
            <div className="box card p-3 mb-3">
              <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                <StatusBadge tone={GNRE_STATUS_TONE[g.status]}>{GNRE_STATUS_LABEL[g.status]}</StatusBadge>
                <span>
                  Valor <strong data-testid="gnre-amount">{formatBRL(g.amountCents)}</strong>
                </span>
                <span>
                  Vencimento <strong>{formatDateBR(g.dueDate)}</strong>
                </span>
              </div>
              <dl className="row mb-0">
                <Info label="Receita">
                  {g.revenueCode} · {guideTitle(g)}
                </Info>
                <Info label="NF-e">
                  <Link to="/vendas/notas-fiscais/$id" params={{ id: g.fiscalDocumentId }}>
                    nº {g.invoiceNumber} série {g.invoiceSeries}
                  </Link>
                  <div className="small text-muted text-break">{g.accessKey}</div>
                </Info>
                <Info label="Destinatário">{g.recipientName ?? "—"}</Info>
                <Info label="Referência">{periodLabel(g.referencePeriod)}</Info>
                <Info label="Linha digitável">
                  <span className="font-monospace" data-testid="gnre-line">
                    {collectionLineLabel(g.digitableLine)}
                  </span>
                </Info>
                <Info label="Nº de controle">{g.controlNumber ?? "—"}</Info>
                {g.issuedAt && <Info label="Emitida em">{dateTimeBR(g.issuedAt)}</Info>}
                {g.paidAt && <Info label="Paga em">{formatDateBR(g.paidAt)}</Info>}
                {g.canceledAt && (
                  <Info label="Cancelada em">
                    {dateTimeBR(g.canceledAt)} — {g.cancelReason}
                  </Info>
                )}
              </dl>
            </div>
            {g.status === "pending" && (
              <div className="box card p-3 mb-3">
                <h2 className="h6">Como emitir</h2>
                <ol className="small mb-0">
                  {g.kind === "gnre" ? (
                    <li>
                      Baixe o XML (ou selecione várias guias na lista e baixe o lote) e envie no Portal GNRE
                      (gnre.pe.gov.br), ou digite os dados do PDF de conferência no portal.
                    </li>
                  ) : (
                    <li>Emita o DARE no portal da SEFAZ-SP com os dados do PDF de conferência.</li>
                  )}
                  <li>Copie a linha digitável da guia emitida e use “Marcar como emitida”.</li>
                  <li>Depois de pagar, use “Marcar como paga” e anexe o comprovante.</li>
                </ol>
              </div>
            )}
          </div>
          <aside className="col-xl-4">
            <div className="box card p-3 mb-3">
              <h2 className="h6">Conta a pagar</h2>
              {g.payable ? (
                <>
                  <p className="mb-2 small">
                    {formatBRL(g.payable.amountCents)} · vencimento {formatDateBR(g.payable.dueDate)} · pago{" "}
                    {formatBRL(g.payable.paidCents)}
                  </p>
                  <Link to="/financas/contas-a-pagar/$id" params={{ id: g.payable.id }} className="small">
                    Abrir conta a pagar
                  </Link>
                </>
              ) : (
                <p className="text-muted small mb-0">Sem conta a pagar.</p>
              )}
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Comprovante</h2>
              {g.proof ? (
                <p className="small mb-2">
                  <button
                    type="button"
                    className="btn btn-link p-0 align-baseline"
                    disabled={proofLink.isPending}
                    onClick={() => proofLink.mutate()}
                  >
                    {g.proof.fileName}
                  </button>{" "}
                  <span className="text-muted">enviado em {dateTimeBR(g.proof.uploadedAt)}</span>
                </p>
              ) : (
                <p className="text-muted small mb-2">Nenhum comprovante.</p>
              )}
              {canUpdate && g.status !== "canceled" && (
                <>
                  <label className="form-label small" htmlFor="gnre-proof">
                    {g.proof ? "Trocar comprovante" : "Anexar comprovante"} (PDF, JPG ou PNG até 5 MB)
                  </label>
                  <input
                    id="gnre-proof"
                    ref={fileRef}
                    type="file"
                    className="form-control form-control-sm"
                    accept="application/pdf,image/jpeg,image/png"
                    disabled={proof.isPending}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      proof.mutate(f, {
                        onSuccess: (v) => {
                          toast("success", "Comprovante anexado");
                          done(v);
                        },
                        onError,
                        onSettled: () => {
                          if (fileRef.current) fileRef.current.value = "";
                        },
                      });
                    }}
                  />
                </>
              )}
            </div>
          </aside>
          <IssueDialog open={dialog === "issue"} guide={g} onClose={() => setDialog(null)} onDone={done} />
          <PayDialog open={dialog === "pay"} guide={g} onClose={() => setDialog(null)} onDone={done} />
          <DueDateDialog open={dialog === "due"} guide={g} onClose={() => setDialog(null)} onDone={done} />
          <CancelGuideDialog
            open={dialog === "cancel"}
            guide={g}
            onClose={() => setDialog(null)}
            onDone={done}
          />
        </div>
      )}
    </>
  );
}
